import { FC, ReactNode, useMemo, useState } from "react";
import { useQuery } from "@apollo/react-hooks";
import _ from "lodash";
import { Icon } from "@/components";
import { Button, HeaderActions, Modal, Tooltip } from "@/ui";
import { AlgoliaOrder, Order } from "@/types";
import { useFixedBodyOnPopup, useMeta } from "@/hooks";
import { QUERY_GET_ORDER } from "apollo/module-operations/order";
import { QUERY_GET_STORE } from "apollo/module-operations/store";
import { useOrdersData } from "../context/OrdersDataContext";
import OrderForm from "../OrderForm";
import { printInvoice, printSlip } from "../print";
import { useOrderStatusActions } from "../OrderActionButtons";
import styles from "./KanbanModal.module.scss";

const PrintIcon = () => <Icon icon="print" />;
const RefreshIcon = () => <Icon icon="refresh" />;
const ReceiptIcon = () => <Icon icon="receipt" />;

export type KanbanModalViewProps = {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    title?: ReactNode;
    children: ReactNode;
    footer: ReactNode;
};

/**
 * Presentational order-detail modal shell (DS).
 * Connected `KanbanModal` keeps GraphQL, print, and action wiring.
 */
export const KanbanModalView: FC<KanbanModalViewProps> = ({
    open,
    onOpenChange,
    title = "Order details",
    children,
    footer,
}) => (
    <Modal
        assistiveDescription="Order details dialog."
        bodyScroll={false}
        className={styles.panel}
        footer={<div className={styles.footer}>{footer}</div>}
        onOpenChange={onOpenChange}
        open={open}
        showCloseButton
        size="lg"
        title={title}
    >
        {children}
    </Modal>
);

export type KanbanModalFooterProps = {
    loading: boolean;
    onViewReceipt: () => void;
    onPrintInvoice: () => void;
    onPrintPackaging: () => void;
    onRefresh: () => void;
    statusActions: ReturnType<typeof useOrderStatusActions>;
};

export const KanbanModalFooter: FC<KanbanModalFooterProps> = ({
    loading,
    onViewReceipt,
    onPrintInvoice,
    onPrintPackaging,
    onRefresh,
    statusActions,
}) => {
    const overflowActions = useMemo(
        () => [
            {
                id: "print",
                label: "Print",
                icon: <PrintIcon />,
                variant: "secondary" as const,
                menuItems: [
                    {
                        id: "invoice",
                        label: "Invoice",
                        icon: <ReceiptIcon />,
                        onSelect: onPrintInvoice,
                    },
                    {
                        id: "packaging",
                        label: "Packaging slips",
                        icon: <ReceiptIcon />,
                        onSelect: onPrintPackaging,
                    },
                ],
            },
            {
                id: "refresh",
                label: "Refresh",
                icon: <RefreshIcon />,
                variant: "secondary" as const,
                onClick: onRefresh,
            },
            ...statusActions,
        ],
        [onPrintInvoice, onPrintPackaging, onRefresh, statusActions],
    );

    if (loading) {
        return (
            <>
                <div className={styles.footerDesktop}>
                    <div className={styles.skeletonBtn} />
                    <div className={styles.footerActions}>
                        <div className={styles.skeletonBtn} />
                        <div className={styles.skeletonBtn} />
                    </div>
                </div>
                <div className={styles.footerMobile}>
                    <div className={styles.skeletonIcon} />
                </div>
            </>
        );
    }

    return (
        <>
            <div className={styles.footerDesktop}>
                <Tooltip content="Open the order receipt PDF" side="top">
                    <Button
                        iconLeft={<ReceiptIcon />}
                        onClick={onViewReceipt}
                        type="button"
                        variant="secondary"
                    >
                        View receipt
                    </Button>
                </Tooltip>
                <div className={styles.footerActions}>
                    {statusActions.map(action => {
                        const button = (
                            <Button
                                className={action.className}
                                disabled={action.disabled || action.loading}
                                iconLeft={
                                    action.loading ? undefined : action.icon
                                }
                                loading={action.loading}
                                onClick={action.onClick}
                                type="button"
                                variant={action.variant ?? "primary"}
                            >
                                {action.label}
                            </Button>
                        );

                        if (action.tooltip) {
                            return (
                                <Tooltip
                                    content={action.tooltip}
                                    key={action.id}
                                    side="top"
                                >
                                    {button}
                                </Tooltip>
                            );
                        }

                        return <span key={action.id}>{button}</span>;
                    })}
                </div>
            </div>
            <div className={styles.footerMobile}>
                <HeaderActions
                    actions={overflowActions}
                    aria-label="Order actions"
                    side="top"
                />
            </div>
        </>
    );
};

interface ModalProps {
    isModalOpen: boolean;
    handleModalToggle: (order: AlgoliaOrder) => void;
    algoliaOrder: AlgoliaOrder;
}

const KanbanModal: FC<ModalProps> = ({
    isModalOpen,
    handleModalToggle,
    algoliaOrder,
}) => {
    const { refetch: refresh } = useOrdersData();
    const { user } = useMeta();
    const [order, setOrder] = useState<Order>(null);
    const [storeInformation, setStoreInformation] = useState({});
    useFixedBodyOnPopup(isModalOpen);

    const { loading: loadingOrder, refetch } = useQuery(QUERY_GET_ORDER, {
        skip:
            user.loading ||
            !algoliaOrder.objectID ||
            !user.accountId ||
            !user.storeId,
        notifyOnNetworkStatusChange: true,
        variables: {
            account_id: user.accountId,
            store_id: user.storeId,
            entity_id: algoliaOrder.objectID,
        },
        onCompleted: resp => {
            const order = (resp && resp.getOrder) || {};
            setOrder(order);
        },
    });

    useQuery(QUERY_GET_STORE, {
        variables: { account_id: user.accountId, store_id: user.storeId },
        skip: user.loading || !user.accountId || !user.storeId,
        onCompleted: resp => {
            setStoreInformation(resp.getStore);
        },
    });

    const handleActionCompleted = () => {
        handleModalToggle(algoliaOrder);
        refetch();
        refresh();
    };

    const statusActions = useOrderStatusActions({
        order: order ?? undefined,
        onActionCompleted: handleActionCompleted,
    });

    const handleViewReceipt = () => {
        const pdf = printInvoice([order], storeInformation, "country", "state");
        pdf.open();
    };

    const handlePrintPackaging = () => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const _order = _.cloneDeep(order) as any;
        _order.shippingPackages = _.map(order?.line_items, p => ({
            id: p.id,
            title: p.name,
            qtyInPackage: p.quantity,
        }));
        const pdf = printSlip([_order], storeInformation, "country", "state");
        pdf.open();
    };

    const handleRefresh = () => {
        void refetch();
        refresh();
    };

    const handleOpenChange = (open: boolean) => {
        if (!open) {
            handleModalToggle(algoliaOrder);
        }
    };

    return (
        <KanbanModalView
            footer={
                <KanbanModalFooter
                    loading={loadingOrder || !order}
                    onPrintInvoice={handleViewReceipt}
                    onPrintPackaging={handlePrintPackaging}
                    onRefresh={handleRefresh}
                    onViewReceipt={handleViewReceipt}
                    statusActions={statusActions}
                />
            }
            onOpenChange={handleOpenChange}
            open={isModalOpen}
        >
            <OrderForm algoliaOrder={algoliaOrder} isModal />
        </KanbanModalView>
    );
};

export default KanbanModal;
